/**
 * yanamas V3 API Service
 * 
 * Connects to the integrated yanamas V3 Dream Engine API.
 * Main endpoint: POST /api/horton-v3/dreamer/run
 */
import { BRAND } from '../config/brand'

// Types matching the OpenAPI spec
export interface DreamerRequest {
  message: string
  session_id?: string
  conversation_id?: string
  /** Stable browser chat id; the backend derives a stable engine session from
   *  it so reloading the page resumes the same conversation. */
  chat_id?: string
  turn_number?: number
  persona?: string  // e.g., 'balanced_guide', 'supportive_friend', 'action_coach', 'wise_mentor', 'cheerful_buddy'
  history?: Array<{ role: 'user' | 'assistant'; content: string }>
  // Rejected items context - passed to LLM to avoid suggesting similar items
  rejected_context?: {
    rejected_items: Array<{
      type: string
      title: string
      reason: string
    }>
    note: string
  }
  // Debug flag to get LLM input/output info
  debug?: boolean
  /** Thread-refinement focus: the id of the held Open Thread the user is shaping
   *  in conversation ("Make it real"). The engine enriches that thread instead of
   *  re-extracting parallel items for the same subject. */
  clarifying_thread_id?: string
  /** Proposal-refinement focus: a not-yet-saved proposed item the user is
   *  adding detail to — the engine enriches it instead of duplicating.
   *  lineage = top-down ancestors (dream → …), children = direct children,
   *  so the engine can constrain the turn to this exact subtree. */
  clarifying_item?: {
    type: string
    title: string
    description?: string
    lineage?: Array<{ type: string; title: string }>
    children?: Array<{ type: string; title: string }>
  }
}

export interface HierarchyItem {
  // items_summary also carries world entries (identity/person/place) that the
  // chat renders separately from the hierarchy proper.
  type: 'dream' | 'aspiration' | 'goal' | 'task' | 'action_item' | 'roadblock' | 'knowledge_skill' | 'skill' | 'habit' | 'identity' | 'person' | 'place'
  title: string
  description?: string
  life_area?: string
  /** Flat items_summary entries carry a parent ref so the tree can be rebuilt */
  parent?: { type: string; title: string }
  /** Planner payload bag (scheduling intent, decision fields, …) */
  fields?: Record<string, any>
  /** World-entry metadata (person/place details) */
  metadata?: Record<string, any>
}

export interface ActionItemNode extends HierarchyItem {
  type: 'action_item'
  duration_minutes?: number
}

export interface TaskNode extends HierarchyItem {
  type: 'task'
  action_items?: ActionItemNode[]
}

export interface RoadblockNode extends HierarchyItem {
  type: 'roadblock'
  severity?: 'low' | 'medium' | 'high'
  suggested_solutions?: string[]
}

export interface KnowledgeSkillNode extends HierarchyItem {
  type: 'knowledge_skill'
  current_level?: 'novice' | 'beginner' | 'intermediate' | 'advanced' | 'expert'
  target_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  learning_steps?: string[]
}

export interface GoalNode extends HierarchyItem {
  type: 'goal'
  tasks?: TaskNode[]
  roadblocks?: RoadblockNode[]
  knowledge_skills?: KnowledgeSkillNode[]
}

export interface AspirationNode extends HierarchyItem {
  type: 'aspiration'
  goals?: GoalNode[]
}

export interface DreamNode extends HierarchyItem {
  type: 'dream'
  aspirations?: AspirationNode[]
  goals?: GoalNode[]  // Goals directly under dream (if no aspirations)
}

export interface ComposedResponse {
  thought_process?: string[]
  recommendations?: string[]
  items_summary?: HierarchyItem[]
  items_hierarchy?: DreamNode[]
  clarifying_questions?: string[]
  response_metadata?: {
    has_concrete_items?: boolean
    emotional_tone_used?: 'supportive' | 'practical' | 'gentle' | 'direct'
    primary_focus?: 'action_oriented' | 'emotional_support' | 'reflection'
  }
  /** Per-turn model cost telemetry (when the backend reports it) */
  cost_info?: {
    message_cost?: number
    [key: string]: any
  }
}

export interface DreamHierarchyFlat {
  aspirations?: any[]
  dreams?: any[]
  goals?: any[]
  habits?: any[]
  roadblocks?: any[]
  skills?: any[]
  tasks?: any[]
}

export interface AnalysisResults {
  intent_type?: string
  intent_summary?: string
  affect_state?: {
    valence?: number
    arousal?: number
    dominant_emotions?: string[]
  }
  flow_decision?: any
  memory_analysis?: any
  world_facts?: any
}

export interface ContextStatus {
  status: 'ok' | 'yellow' | 'red' | 'critical'
  current_tokens: number
  max_tokens: number
  remaining_tokens: number
  percentage_used: number
  can_continue: boolean
  user_message?: string
}

export interface DreamerResponse {
  success: boolean
  message?: string
  latency_ms?: number
  response?: string
  composed_response?: ComposedResponse
  dream_hierarchy?: DreamHierarchyFlat
  agent_outputs?: {
    goal_updates?: any
    task_updates?: any
    skill_updates?: any
  }
  analysis?: AnalysisResults
  pipeline?: {
    trivial_router_result?: any
    skip_full_pipeline?: boolean
    pre_safety_flags?: string[]
    final_safety_flags?: string[]
    reward_scores?: any
    reflection_snippet?: string
  }
  context_status?: ContextStatus
  session_id?: string
  conversation_id?: string
  turn_number?: number
  /** Engine that handled this turn: "compass" | "lifecoach" | "horton_guidance". */
  runtime?: string
  /** This turn's gateway spend in USD, when the engine reports it (lifecoach). */
  cost_total?: number
  cost_lifetime?: number | null
  /** The ENGINE's turn row id (lifecoach/compass). The voice route speaks a
   * RECORDED turn, so this is what gets sent back to play a reply. */
  turn_id?: number
  error?: string
  telemetry?: {
    nodes: Array<{
      node: string
      model: string
      /** The vendor that served this pass. `(unknown)` when no attempt named one. */
      provider: string
      input_tokens: number
      output_tokens: number
      cache_read_tokens: number
      /** Output tokens spent thinking; already inside `output_tokens`. */
      reasoning_tokens?: number
      latency_ms: number
      cost_usd: number
      /** Attempts made for this pass. Above 1 means it was paid for more than once. */
      attempts?: number
      errors?: number
    }>
    total_cost_usd: number
    total_input_tokens: number
    total_output_tokens: number
    total_cache_read_tokens: number
    total_reasoning_tokens?: number
    total_tokens: number
    total_llm_calls: number
    total_latency_ms: number
    pipeline_had_revision: boolean
    providers: Array<{ provider: string; model_count: number; total_tokens: number; total_cost_usd: number }>
  }
  /** Embedded AI core debug and/or external chat-engine `meta.debug` (e.g. `llm_calls`). */
  debug?: Record<string, unknown>
}

export interface AgentInfo {
  id: string
  name: string
  description: string
  icon: string
  recommended_bucket: string
  system_prompt?: string
}

export interface PersonaInfo {
  id: string
  name: string
  emoji: string
  tagline: string
  style?: {
    tone?: string
    formality?: string
    emoji_usage?: string
    energy?: string
  }
  behavior?: any
}

// API Base URL
const API_BASE = '/api/horton-v3'

/**
 * Run the Dream Engine pipeline
 */
export async function runDreamer(request: DreamerRequest): Promise<DreamerResponse> {
  const debugParam = request.debug ? '?debug=true' : ''
  const response = await fetch(`${API_BASE}/dreamer/run${debugParam}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Simple chat endpoint
 */
export async function chat(
  messages: Array<{ role: string; content: string }>,
  bucket: string = 'balanced',
  provider?: string
): Promise<any> {
  const response = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ messages, bucket, provider }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Get list of available agents
 */
export async function getAgents(): Promise<{ agents: Record<string, AgentInfo> }> {
  const response = await fetch(`${API_BASE}/agents`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Get list of available personas
 */
export async function getPersonas(): Promise<{ personas: Record<string, PersonaInfo> }> {
  const response = await fetch(`${API_BASE}/personas`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Get system configuration (buckets, providers)
 */
export async function getConfig(): Promise<{ buckets: string[]; providers: Record<string, any[]> }> {
  const response = await fetch(`${API_BASE}/config`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{ status: string; langgraph_available: boolean }> {
  const response = await fetch(`${API_BASE}/health`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Which engine this user is routed to ("compass" | "lifecoach" |
 * "horton_guidance"). Empty string when unknown. Used to pick the chat shell.
 */
export async function getRuntime(): Promise<string> {
  const response = await fetch(`${API_BASE}/runtime`, { credentials: 'include' })
  if (!response.ok) return ''
  const data = await response.json().catch(() => ({}))
  return (data && data.runtime) || ''
}

// ── Lifecoach consolidation-built Life Map ─────────────────────────────
export interface LifeMapItem {
  id: number
  kind: string
  title: string
  status: string
  confidence: number
  life_area: string | null
  life_areas?: string[]
  display_title?: string | null
  cadence?: string | null
  rationale?: string | null
  summary?: string | null
  /** A task the person gave no purpose for — an errand. It has no parent by
   * rule and is left out of the narrative; the panel gathers these under
   * one heading instead of showing them as orphans of a tree they were
   * never part of. */
  ad_hoc?: boolean
  /** No when was ever stated, and the engine will not invent one. */
  needs_when?: boolean
  temporal?: Record<string, any> | null
  // RFC 5545, compiled by the engine from the stated indicators: one rule per
  // distinct time (a day may have its own hour), `rrule` when exactly one,
  // `text` the same human line the engine's prompts show.
  schedule?: { rrules: string[]; rrule: string | null; text: string } | null
  location?: string | null
  evidence?: number
  /** The engine turns this item was said in (evidence rows and what they
   *  refined). Stamped on the page copy at acceptance so a chat reminder from
   *  one of those turns binds to it by id — never by matching words. */
  turn_ids?: number[]
  supports?: string | null
  area_locked?: boolean
  supports_nothing?: boolean
  mirror_attempts?: number
}
export interface LifeMapEdge { src: number; dst: number; kind: string }
export interface LifeMapArea { slug: string; label: string; source?: string; rationale?: string | null }
export interface LifeMapNarrative {
  version: number
  body: string
  themes?: string[]
  actual_self?: string | null
  ideal_self?: string | null
}
export interface LifeMapResponse {
  available: boolean
  runtime?: string
  map: LifeMapItem[]
  map_edges: LifeMapEdge[]
  areas: LifeMapArea[]
  narrative?: LifeMapNarrative | null
  observations_total?: number
  counts?: Record<string, number>
  cost_total?: number
  cost_lifetime?: number | null
  failed_call_cost?: number
}

/**
 * Fetch the lifecoach consolidation-built Life Map for the current user.
 * `available:false` for non-lifecoach users (or when the sidecar is down).
 */
export async function getLifeMap(): Promise<LifeMapResponse> {
  const empty: LifeMapResponse = { available: false, map: [], map_edges: [], areas: [] }
  try {
    const response = await fetch(`${API_BASE}/lifemap`, { credentials: 'include' })
    if (!response.ok) return empty
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') return empty
    return {
      available: Boolean(data.available),
      runtime: data.runtime,
      map: Array.isArray(data.map) ? data.map : [],
      map_edges: Array.isArray(data.map_edges) ? data.map_edges : [],
      areas: Array.isArray(data.areas) ? data.areas : [],
      cost_lifetime: typeof data.cost_lifetime === 'number' ? data.cost_lifetime : undefined,
      narrative: data.narrative ?? null,
      observations_total: data.observations_total,
      counts: data.counts,
      cost_total: data.cost_total,
      failed_call_cost: data.failed_call_cost,
    }
  } catch {
    return empty
  }
}

// ── §8 sticky note + #42 regroup (lifecoach panel) ───────────────────────────

export interface AttentionResponse {
  available: boolean
  working_on: Array<Record<string, unknown> & { kind: string }>
  need_you: Array<Record<string, unknown> & { kind: string }>
  counts: { working_on: number; need_you: number }
  last_run?: { id: number; kind: string; finished_at: string | null } | null
}

export interface RegroupProposal {
  proposal_id: number
  status: string
  created_at: string
  expires_at: string
  payload: {
    summary?: Record<string, number>
    edges_add?: Array<{ src: number; dst: number; kind: string }>
    edges_remove?: Array<{ src: number; dst: number; kind: string; reason?: string; detail?: string }>
    edges_kept?: Array<{ src: number; dst: number; child_title?: string; parent_title?: string }>
    retags?: unknown[]
    retires?: unknown[]
    defects?: Array<{ guard: string; detail: string }>
    orphans_with_candidate_parents?: Array<{ id: number; title: string; candidate_parent_titles?: string[] }>
    preview?: Array<{ id: number; title: string; kind: string; parent_before: string | null; parent_after: string | null }>
  }
}

/** The sticky note: what the engine owes the map, and what it needs you for. */
export async function getAttention(): Promise<AttentionResponse> {
  const empty: AttentionResponse = { available: false, working_on: [], need_you: [],
    counts: { working_on: 0, need_you: 0 } }
  try {
    const response = await fetch(`${API_BASE}/attention`, { credentials: 'include' })
    if (!response.ok) return empty
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') return empty
    return {
      available: Boolean(data.available),
      working_on: Array.isArray(data.working_on) ? data.working_on : [],
      need_you: Array.isArray(data.need_you) ? data.need_you : [],
      counts: data.counts ?? { working_on: 0, need_you: 0 },
      last_run: data.last_run ?? null,
    }
  } catch {
    return empty
  }
}

export async function getRegroupPending(): Promise<RegroupProposal | null> {
  try {
    const response = await fetch(`${API_BASE}/regroup`, { credentials: 'include' })
    if (!response.ok) return null
    const data = await response.json().catch(() => null)
    return (data && data.pending) ? (data.pending as RegroupProposal) : null
  } catch {
    return null
  }
}

/** Ask the engine to draft a regroup. Resolves to the proposal, or
 *  `{status:'no_change'}` when the map has nothing to move (a real answer). */
export async function proposeRegroup(): Promise<RegroupProposal | { status: 'no_change'; payload?: unknown } | null> {
  try {
    const response = await fetch(`${API_BASE}/regroup`, { method: 'POST', credentials: 'include' })
    if (!response.ok) return null
    return (await response.json().catch(() => null)) as RegroupProposal | null
  } catch {
    return null
  }
}

export async function decideRegroup(proposalId: number, decision: 'accept' | 'reject'):
    Promise<{ ok: boolean; status?: string; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/regroup/decide`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proposal_id: proposalId, decision }),
    })
    const data = await response.json().catch(() => ({}))
    return { ok: response.ok, status: data?.status, error: data?.error ?? data?.detail }
  } catch (e) {
    return { ok: false, error: (e as Error).message }
  }
}

// ── "being organized" surface + Analyze button ──────────────────────────────

export interface PendingObservation {
  observation_id: number
  turn_id: number | null
  gist: string
  kind: string | null
  said_at: string
  sensitive: boolean
  /** Which surface it came from: 'diary' | null (a conversation). A diary
   *  commitment written on one day is HELD by the engine until it recurs, so
   *  "Analyze my map" will not move it onto the map — the strip has to say
   *  so rather than imply otherwise. */
  source?: string | null
}

export interface PendingResponse {
  available: boolean
  pending: PendingObservation[]
  count: number
  can_analyze: boolean
  last_organized_at?: string | null
}

/** What Compass heard but has not yet organized into map items. A pure
 *  projection — no model call — so it can be polled after every turn. */
export async function getPending(): Promise<PendingResponse> {
  const empty: PendingResponse = { available: false, pending: [], count: 0, can_analyze: false }
  try {
    const response = await fetch(`${API_BASE}/pending`, { credentials: 'include' })
    if (!response.ok) return empty
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') return empty
    return {
      available: Boolean(data.available),
      pending: Array.isArray(data.pending) ? data.pending : [],
      count: typeof data.count === 'number' ? data.count : 0,
      can_analyze: Boolean(data.can_analyze),
      last_organized_at: data.last_organized_at ?? null,
    }
  } catch {
    return empty
  }
}

/** "Analyze my map": run consolidation on demand. Resolves to the run's
 *  status ('applied' | 'already_running' | …) and how many items changed. */
export async function analyzeMap():
    Promise<{ ok: boolean; status?: string; mutations?: number; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/analyze`, { method: 'POST', credentials: 'include' })
    const data = await response.json().catch(() => ({}))
    return { ok: response.ok, status: data?.status, mutations: data?.mutations,
             error: data?.error }
  } catch (e) {
    return { ok: false, error: (e as Error).message }
  }
}

// ── who the engine knows ────────────────────────────────────────────────────
// The People screen renders the graph's `life_nodes` — which a lifecoach user
// never writes to, because their turns are handled by the sidecar. Everyone
// they mention lands in ITS entity axis instead. This is that list.

export interface CapturedPerson {
  id: number
  name: string
  kind: string | null
  /** In their own words: "my daughter", "mi madre". null = never stated. */
  relation_to_user: string | null
  aliases: string[]
  /** Recent things they said about this person, already phrased as what they
   *  are — a standing fact reads plainly, an impression as "you've said …". */
  notes: string[]
  /** Notes law 8 bars from being quoted back. Counted, never shown. */
  notes_withheld: number
  relations: string[]
  items: Array<{ id: number; kind: string; title: string; display_title?: string | null }>
  last_seen: string | null
  /** False when the person only ever referred to them BY the relationship
   *  ("my daughter") and never gave a name. */
  named: boolean
  /** The person's OWN filing — a group is a tag. Never model-written. */
  tags: string[]
  /** They have corrected this record by hand, so capture adds to it but no
   *  longer rewrites the name or the relationship. */
  curated?: boolean
  /** A sentence or two about them, rolled up on the cold plane from their own
   *  notes and mentions. Null until there is enough to say. */
  summary?: string | null
}

export interface EnginePeopleResponse {
  available: boolean
  people: CapturedPerson[]
  counts: Record<string, number>
}

export async function getEnginePeople(): Promise<EnginePeopleResponse> {
  const empty: EnginePeopleResponse = { available: false, people: [], counts: {} }
  try {
    const response = await fetch(`${API_BASE}/people`, { credentials: 'include' })
    if (!response.ok) return empty
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') return empty
    return {
      available: Boolean(data.available),
      people: Array.isArray(data.people) ? data.people : [],
      counts: data.counts && typeof data.counts === 'object' ? data.counts : {},
    }
  } catch {
    return empty
  }
}

/** One dated line about a person. `source`: null = said in the chat, 'diary'
 *  = written in a diary, 'user' = typed on their page. Only the first two are
 *  evidence of what the engine heard, which is why they stay distinguishable. */
export interface PersonNote {
  id: number
  ts: string
  note: string
  kind: string | null            // 'attribute' (a standing fact) | 'impression'
  source: string | null
  edited: boolean
  turn_id: number | null
  observation_id: number | null
}

/** A reference that NAMED this person: evidence, and never editable — a
 *  record you can rewrite has stopped being one. */
export interface PersonMention {
  observation_id: number
  ts: string
  gist: string
  kind: string | null
  source: string | null
  turn_id: number | null
  sensitive: boolean
}

export interface PersonPage {
  person: CapturedPerson & { relations: string[] }
  said: PersonNote[]
  said_withheld: number
  heard: PersonMention[]
  map: Array<{ id: number; kind: string; title: string; display_title?: string | null }>
}

export async function getPerson(entityId: number): Promise<PersonPage | null> {
  try {
    const r = await fetch(`${API_BASE}/person?entity_id=${entityId}`, { credentials: 'include' })
    if (!r.ok) return null
    return (await r.json().catch(() => null)) as PersonPage | null
  } catch {
    return null
  }
}

type Wrote = { ok: boolean; error?: string; data?: any }

async function write(path: string, body: unknown): Promise<Wrote> {
  try {
    const r = await fetch(`${API_BASE}${path}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await r.json().catch(() => ({}))
    // They pressed save. A failure is reported, never degraded to a stub —
    // the read paths above may do that because the data is still on screen.
    return r.ok ? { ok: true, data } : { ok: false, error: data?.error ?? `save failed (${r.status})` }
  } catch (e) {
    return { ok: false, error: (e as Error).message }
  }
}

/** Their correction of their own record. Only the fields passed are touched. */
export function updatePerson(entityId: number, fields: {
  name?: string; relation_to_user?: string | null
  aliases?: string[]; tags?: string[]
}): Promise<Wrote> {
  return write('/person', { entity_id: entityId, ...fields })
}

export function addPersonNote(entityId: number, note: string, kind: 'attribute' | 'impression') {
  return write('/person/note', { entity_id: entityId, note, kind })
}

export function editPersonNote(noteId: number, note: string, kind?: 'attribute' | 'impression') {
  return write('/person/note', { op: 'edit', note_id: noteId, note, ...(kind ? { kind } : {}) })
}

export function deletePersonNote(noteId: number) {
  return write('/person/note', { op: 'delete', note_id: noteId })
}

/** "These two are the same person." The dropped row's name survives as an
 *  alias so the next mention folds onto the survivor. */
export function mergePeople(keepId: number, dropId: number) {
  return write('/person/merge', { keep_id: keepId, drop_id: dropId })
}

export function forgetPerson(entityId: number) {
  return write('/person/forget', { entity_id: entityId })
}

/** A decision the person made about their own roster. The engine shows these
 *  to its reconciliation pass so it never proposes undoing one. */
export interface PersonCorrection {
  kind: 'merged' | 'renamed' | 'relation' | 'forgotten' | string
  subject: string
  detail: string | null
  entity_id: number | null
  ts: string
}

export async function getPersonCorrections(): Promise<PersonCorrection[]> {
  try {
    const r = await fetch(`${API_BASE}/person/corrections`, { credentials: 'include' })
    if (!r.ok) return []
    const data = await r.json().catch(() => null)
    return Array.isArray(data?.corrections) ? data.corrections : []
  } catch {
    return []
  }
}

export interface TagCount { tag: string; count: number }

export async function getTags(): Promise<TagCount[]> {
  try {
    const r = await fetch(`${API_BASE}/tags`, { credentials: 'include' })
    if (!r.ok) return []
    const data = await r.json().catch(() => null)
    return Array.isArray(data?.tags) ? data.tags : []
  } catch {
    return []
  }
}

// ── the diary ───────────────────────────────────────────────────────────────
// A second surface, not the chat. The person writes their day; Compass says a
// few words back and never asks a question. What the engine LEARNS from an
// entry shows up on the Life Map days later, and deliberately not here.

export interface DiaryEntry {
  turn_id: number
  ts: string
  entry: string
  acknowledgement: string
}

export interface DiaryPage {
  available: boolean
  entries: DiaryEntry[]
  next_before_id?: number | null
}

export interface DiaryWriteResult {
  ok: boolean
  turn_id?: number
  acknowledgement?: string
  /** How many observations the entry produced. 0 is not a failure — some days
   *  genuinely hold nothing to record. */
  recorded?: number
  error?: string
}

/** Read the diary back, newest first. `beforeId` pages further back. */
export async function getDiary(limit = 30, beforeId?: number): Promise<DiaryPage> {
  const empty: DiaryPage = { available: false, entries: [] }
  try {
    const qs = new URLSearchParams({ limit: String(limit) })
    if (beforeId) qs.set('before_id', String(beforeId))
    const response = await fetch(`${API_BASE}/diary?${qs}`, { credentials: 'include' })
    if (!response.ok) return empty
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') return empty
    return {
      available: Boolean(data.available),
      entries: Array.isArray(data.entries) ? data.entries : [],
      next_before_id: data.next_before_id ?? null,
    }
  } catch {
    return empty
  }
}

/**
 * Write one entry.
 *
 * Not best-effort, unlike every read above: by the time this resolves the
 * person's own words have left the textarea, so a failure has to be reported
 * as one and the caller has to keep the text. `local_date` is the writer's
 * calendar date — a diary entry belongs to the day they lived, not to
 * whatever day it is on the server — and `occurredAt` moves the whole entry
 * to an earlier moment, for writing up last night or catching up on a week.
 */
export async function writeDiary(entry: string, occurredAt?: Date): Promise<DiaryWriteResult> {
  const when = occurredAt ?? new Date()
  const localDate = when.toLocaleDateString('en-CA')           // YYYY-MM-DD, local
  try {
    const response = await fetch(`${API_BASE}/diary`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // `occurred_at` carries the writer's own offset, so an entry about
      // last night is filed under last night wherever the server is.
      body: JSON.stringify({ entry, local_date: localDate,
                             ...(occurredAt ? { occurred_at: when.toISOString() } : {}) }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { ok: false, error: data?.error ?? data?.detail ?? `the entry did not reach ${BRAND.assistant}` }
    }
    return {
      ok: true,
      turn_id: data?.turn_id,
      acknowledgement: typeof data?.acknowledgement === 'string' ? data.acknowledgement : '',
      recorded: typeof data?.recorded === 'number' ? data.recorded : undefined,
    }
  } catch (e) {
    return { ok: false, error: (e as Error).message }
  }
}

// Export default service object
export const hortonV3Service = {
  runDreamer,
  chat,
  getAgents,
  getPersonas,
  getConfig,
  healthCheck,
  getRuntime,
  getLifeMap,
  getAttention,
  getPending,
  analyzeMap,
  getRegroupPending,
  proposeRegroup,
  decideRegroup,
  getDiary,
  writeDiary,
  getEnginePeople,
  getPerson,
  updatePerson,
  addPersonNote,
  editPersonNote,
  deletePersonNote,
  mergePeople,
  forgetPerson,
  getTags,
  getPersonCorrections,
}

export default hortonV3Service

